import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  BugReport as BugReportIcon,
  Warning as WarningIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Assessment as BarChartIcon
} from '@mui/icons-material';
import DashboardApiService from '../../services/dashboard/DashboardApiService';
import MitreApiService from '../../services/mitre/MitreApiService';

const MitreAttack = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [mitreData, setMitreData] = useState(null);
  const [selectedTactic, setSelectedTactic] = useState('');
  const [tacticDetails, setTacticDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTactic, setLoadingTactic] = useState(false);
  const [error, setError] = useState('');
  const [fetchingAgents, setFetchingAgents] = useState(true);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setFetchingAgents(true);
      const agentsData = await DashboardApiService.getAllAgents();
      setAgents(agentsData || []);
      
      // Set default agent selection to first agent
      if (agentsData && agentsData.length > 0 && !selectedAgent) {
        setSelectedAgent(agentsData[0].id);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setError('Failed to fetch agents');
    } finally {
      setFetchingAgents(false);
    }
  };

  const handleFetchMitreData = async () => {
    if (!selectedAgent) {
      setError('Please select an agent');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSelectedTactic('');
      setTacticDetails(null);
      
      const response = await MitreApiService.getMitreAttackData(selectedAgent, 30);
      setMitreData(response);
    } catch (error) {
      console.error('Error fetching MITRE ATT&CK data:', error);
      setError('Failed to fetch MITRE ATT&CK data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTacticSelect = async (tactic) => {
    if (!selectedAgent || !tactic) return;

    try {
      setLoadingTactic(true);
      setSelectedTactic(tactic);
      
      const [events, techniques] = await Promise.all([
        MitreApiService.getMitreEventsByTactic(selectedAgent, tactic, 30),
        MitreApiService.getTechniquesByTactic(selectedAgent, tactic, 30)
      ]);
      
      setTacticDetails({
        tactic,
        events,
        techniques,
        count: events.length
      });
    } catch (error) {
      console.error('Error fetching tactic details:', error);
      setError('Failed to fetch tactic details. Please try again.');
    } finally {
      setLoadingTactic(false);
    }
  };

  const getMitreSummary = () => {
    if (!mitreData) return null;
    return MitreApiService.getMitreSummary(mitreData);
  };

  const formatTopTacticsForChart = () => {
    if (!mitreData || !mitreData.top_tactics) return [];
    
    return mitreData.top_tactics.slice(0, 8).map((tactic, index) => ({
      name: tactic.tactic,
      value: tactic.count,
      fill: getColorForIndex(index)
    }));
  };

  const getColorForIndex = (index) => {
    const colors = [
      '#6366f1', '#8b5cf6', '#06b6d4', '#10b981', 
      '#f59e0b', '#ef4444', '#ec4899', '#84cc16'
    ];
    return colors[index % colors.length];
  };

  const summary = getMitreSummary();
  const chartData = formatTopTacticsForChart();

  if (loading && !mitreData) {
    return (
      <Box className="mitre-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">Loading MITRE ATT&CK data...</Typography>
      </Box>
    );
  }

  if (error && !mitreData) {
    return (
      <Box className="mitre-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={handleFetchMitreData}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          <SecurityIcon sx={{ mr: 2, verticalAlign: 'middle', color: '#6366f1' }} />
          MITRE ATT&CK Framework
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Analyze adversary tactics, techniques, and procedures across your infrastructure
        </Typography>
      </Box>

      {/* Control Panel */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={9}>
              <FormControl fullWidth disabled={fetchingAgents}>
                <Select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Select an Agent to Analyze MITRE ATT&CK Data
                        </Typography>
                      );
                    }
                    const agent = agents.find(a => a.id === selected);
                    return agent ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip 
                          icon={<SecurityIcon />} 
                          label={agent.name} 
                          size="small" 
                          sx={{ mr: 2, fontWeight: 'bold' }} 
                        />
                        <Typography variant="body1" color="text.secondary">
                          {agent.ip}
                        </Typography>
                      </Box>
                    ) : selected;
                  }}
                  sx={{ 
                    backgroundColor: 'white',
                    height: '56px',
                    fontSize: '1rem',
                    '& .MuiSelect-select': {
                      padding: '16px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '56px',
                      boxSizing: 'border-box'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '2px',
                      borderColor: '#d1d5db'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        zIndex: 9999
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    }
                  }}
                >
                  {agents.map((agent) => (
                    <MenuItem 
                      key={agent.id} 
                      value={agent.id}
                      sx={{ 
                        minHeight: '56px',
                        padding: '12px 16px',
                        '&:hover': {
                          backgroundColor: '#f3f4f6'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Chip 
                          icon={<SecurityIcon />} 
                          label={agent.name} 
                          size="small" 
                          sx={{ mr: 2, fontWeight: 'bold' }} 
                        />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {agent.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            IP: {agent.ip} • Status: {agent.status || 'Active'}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleFetchMitreData}
                disabled={!selectedAgent || loading || fetchingAgents}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <BarChartIcon />}
                sx={{ 
                  height: '56px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #4f46e5, #7c3aed)',
                  }
                }}
              >
                {loading ? 'Loading...' : 'ANALYZE THREATS'}
              </Button>
            </Grid>
          </Grid>

          {fetchingAgents && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, p: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Loading agents...</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary.totalEvents.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Events
                    </Typography>
                  </Box>
                  <SecurityIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary.uniqueTactics}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Unique Tactics
                    </Typography>
                  </Box>
                  <ShieldIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary.uniqueTechniques}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Techniques
                    </Typography>
                  </Box>
                  <BugReportIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {summary.criticalEvents.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Critical Events
                    </Typography>
                  </Box>
                  <WarningIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Charts and Tables Section */}
      {mitreData && mitreData.top_tactics && (
        <Box sx={{ mb: 4 }}>
          {/* MITRE ATT&CK Tactics Distribution - Full Width */}
          <Card sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            border: '2px solid #e2e8f0'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  MITRE ATT&CK Tactics Distribution
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Interactive visualization of attack tactics across your infrastructure
                </Typography>
              </Box>
              <Divider sx={{ mb: 4 }} />
              
              <Box sx={{ 
                width: '100%', 
                height: 700,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={150}
                        outerRadius={280}
                        paddingAngle={2}
                        dataKey="value"
                        isAnimationActive={true}
                        animationBegin={0}
                        animationDuration={2000}
                        style={{ cursor: 'pointer' }}
                      >
                        {chartData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.fill}
                            stroke="#fff"
                            strokeWidth={3}
                            onClick={() => handleTacticSelect(entry.name)}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [value.toLocaleString(), 'Events']}
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '2px solid #6366f1',
                          borderRadius: '16px',
                          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          padding: '12px 16px'
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={100}
                        iconType="circle"
                        wrapperStyle={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          paddingTop: '20px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                      No data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Top MITRE ATT&CK Tactics - Separate Section */}
          <Card sx={{ 
            background: 'linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%)',
            border: '2px solid #d8b4fe'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <BugReportIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Top Attack Tactics Breakdown
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Detailed ranking and analysis of most frequent attack tactics
                </Typography>
              </Box>
              <Divider sx={{ mb: 4 }} />
              
              <TableContainer component={Paper} sx={{ 
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                borderRadius: '16px',
                overflow: 'hidden'
              }}>
                <Table size="medium">
                  <TableHead sx={{ backgroundColor: '#8b5cf6' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                        Rank
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                        Tactic Name
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                        Event Count
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mitreData.top_tactics.slice(0, 8).map((tactic, index) => (
                      <TableRow 
                        key={tactic.tactic} 
                        hover
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: '#f8fafc',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(odd)': {
                            backgroundColor: '#fafafa'
                          }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={index + 1} 
                            size="medium" 
                            color="primary" 
                            variant="filled"
                            sx={{ 
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              minWidth: '40px'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            {tactic.tactic}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={tactic.count.toLocaleString()} 
                            size="medium" 
                            color="secondary"
                            variant="filled"
                            sx={{ 
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              minWidth: '60px'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Button
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`/mitre-attack/details/${encodeURIComponent(tactic.tactic)}/${selectedAgent}`)}
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              px: 3,
                              py: 1,
                              borderRadius: '8px',
                              background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #4f46e5, #7c3aed)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 16px rgba(99, 102, 241, 0.3)'
                              }
                            }}
                          >
                            VIEW DETAILS
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tactic Details Section */}
      {tacticDetails && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                {tacticDetails.tactic} - Detailed Analysis
              </Typography>
              <Chip 
                label={`${tacticDetails.count} Events`}
                color="primary"
                variant="outlined"
              />
            </Box>
            
            <Divider sx={{ mb: 3 }} />

            {loadingTactic ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading tactic details...</Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Techniques Used
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Technique</TableCell>
                          <TableCell>ID</TableCell>
                          <TableCell>Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tacticDetails.techniques.slice(0, 10).map((technique, index) => (
                          <TableRow key={`${technique.id}-${technique.technique}`}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {technique.technique}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {technique.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={technique.count} size="small" color="primary" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Recent Events
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Agent</TableCell>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Level</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tacticDetails.events.slice(0, 5).map((event, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {event.agent_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(event.timestamp).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={event.level} 
                                size="small" 
                                color={event.level >= 10 ? 'error' : event.level >= 7 ? 'warning' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions Card */}
      {!mitreData && !loading && (
        <Card sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SecurityIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Welcome to MITRE ATT&CK Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Select an agent from the dropdown above and click "Analyze Threats" to view MITRE ATT&CK framework data
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip icon={<ShieldIcon />} label="Tactics Analysis" variant="outlined" />
              <Chip icon={<BugReportIcon />} label="Techniques Mapping" variant="outlined" />
              <Chip icon={<WarningIcon />} label="Threat Detection" variant="outlined" />
              <Chip icon={<AnalyticsIcon />} label="Security Insights" variant="outlined" />
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default MitreAttack;
